// Task notice in a 1:1 Kjuup chat: the creator of a task told the assignee about it
// (Kjuup-BE TaskNoticeService). The message row stays a normal text message; the card
// comes from `chat.taskNotice` = { todoId, senderId, recipientId, recipientName,
// description, dueAt, roomId, roomName, recipientStatus, closed, acknowledgedAt }.
// The assignee confirms here ("Bestätigen"); the sender sees whether they did.
// Divs only inside bubbles: the bubble CSS forces its own font onto spans.
import React, { useContext } from "react";
import MaterialIcon from "../MaterialIcon";
import LanguageContext from "../../helper/LanguageContext";
import "./TaskNoticeMessage.css";

const ZONE = "Europe/Berlin";

function localeOf(language) {
  const lang = String(language || "de").split("-")[0];
  return lang === "gsw" || lang === "wae" ? "de-CH" : lang;
}

function formatWhen(value, language, withWeekday) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(localeOf(language), {
      ...(withWeekday ? { weekday: "short" } : {}),
      day: "numeric",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: ZONE,
    }).format(date);
  } catch (_) {
    return date.toLocaleString();
  }
}

export default function TaskNoticeMessage({ notice, userId, busy, onConfirm, onOpenTask }) {
  const { strings = {}, isGerm, language } = useContext(LanguageContext) || {};
  const t = (key, en, de) => strings[key] || (isGerm ? de : en);
  if (!notice) return null;

  const isRecipient = userId != null && String(notice.recipientId) === String(userId);
  const acknowledged = !!notice.acknowledgedAt;
  const done = notice.recipientStatus === "completed" || notice.todoStatus === "confirmed";
  const late = !!notice.dueAt && !done && new Date(notice.dueAt).getTime() < Date.now();
  const firstName = String(notice.recipientName || "").split(" ")[0];

  let state;
  if (notice.closed && !acknowledged) {
    state = (
      <div className="kjc-tnotice__state is-muted">
        <MaterialIcon icon="block" size={16} />
        <div>{t("chat_task_notice_closed", "Task no longer open", "Aufgabe nicht mehr offen")}</div>
      </div>
    );
  } else if (acknowledged) {
    const label = isRecipient
      ? t("chat_task_notice_confirmed_you", "You confirmed", "Von dir bestätigt")
      : `${t("chat_task_notice_confirmed_by", "Confirmed by", "Bestätigt von")} ${firstName || ""}`.trim();
    state = (
      <div className="kjc-tnotice__state is-ok">
        <MaterialIcon icon="check_circle" size={16} fill={1} />
        <div>
          {label} · {formatWhen(notice.acknowledgedAt, language, false)}
        </div>
      </div>
    );
  } else if (isRecipient) {
    state = (
      <button type="button" className="kj-btn kj-btn--primary kjc-tnotice__confirm" onClick={() => onConfirm && onConfirm(notice)} disabled={!!busy}>
        <MaterialIcon icon="task_alt" size={18} />
        <div>{t("chat_task_notice_confirm", "Confirm", "Bestätigen")}</div>
      </button>
    );
  } else {
    state = (
      <div className="kjc-tnotice__state is-muted">
        <MaterialIcon icon="schedule" size={16} />
        <div>{t("chat_task_notice_waiting", "Waiting for confirmation", "Wartet auf Bestätigung")}</div>
      </div>
    );
  }

  return (
    <div className="kjc-tnotice">
      <div className="kjc-tnotice__head">
        <div className="kjc-tnotice__icon">
          <MaterialIcon icon="assignment" size={18} />
        </div>
        <div className="kjc-tnotice__kind">{t("chat_task_notice_title", "Task", "Aufgabe")}</div>
        {notice.roomName ? <div className="kjc-tnotice__room">{notice.roomName}</div> : null}
      </div>
      <div className={`kjc-tnotice__text${done ? " is-done" : ""}`}>{notice.description}</div>
      <div className={`kjc-tnotice__due${late ? " is-late" : ""}`}>
        <MaterialIcon icon="event" size={16} />
        <div>
          {notice.dueAt
            ? `${t("chat_task_notice_due", "Complete by", "Abschließen bis")} ${formatWhen(notice.dueAt, language, true)}`
            : t("chat_task_notice_no_due", "No due date", "Ohne Frist")}
        </div>
        {done ? <div className="kjc-tnotice__done">{t("pro_todo_status_done", "Done", "Erledigt")}</div> : null}
      </div>
      <div className="kjc-tnotice__foot">
        {state}
        {onOpenTask && notice.roomId ? (
          <button type="button" className="kj-btn kj-btn--ghost kjc-tnotice__open" onClick={() => onOpenTask(notice)}>
            <div>{t("chat_task_notice_open", "Open task", "Aufgabe öffnen")}</div>
            <MaterialIcon icon="chevron_right" size={18} />
          </button>
        ) : null}
      </div>
    </div>
  );
}
